import React from "react";
import { Link } from "react-router-dom";
import { Check, HelpCircle, Phone } from "lucide-react";
import { servicePages, company, images } from "../mock/mock";
import { PageHero, SectionHeading, PrimaryButton, CTABanner } from "../components/Common";
import Home from "./Home";

const ServicePage = ({ slug }) => {
  const data = servicePages[slug];
  if (!data) return <Home />;

  return (
    <div>
      <PageHero eyebrow={data.kicker} title={data.title} subtitle={data.intro} image={data.heroImage} />

      {/* Overview */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <div className="img-zoom rounded-2xl shadow-xl">
            <img src={data.gallery[0]} alt={data.title} className="w-full h-[420px] object-cover rounded-2xl" />
          </div>
          <div>
            <SectionHeading eyebrow="Overview" title={data.seoTitle} />
            <div className="-mt-6 space-y-4">
              {data.paragraphs.map((p, i) => (
                <p key={i} className="text-muted-foreground leading-relaxed">{p}</p>
              ))}
            </div>
            <div className="mt-8">
              <PrimaryButton to="/contact">Talk to Us Today</PrimaryButton>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="bg-secondary py-20">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <div className="order-2 lg:order-1">
            <SectionHeading eyebrow="Capabilities" title={data.featuresTitle} />
            <div className="-mt-6 grid sm:grid-cols-2 gap-4">
              {data.features.map((f) => (
                <div key={f} className="flex items-start gap-3 bg-white rounded-xl p-4 border border-border card-lift">
                  <div className="w-6 h-6 rounded-full bg-gold flex items-center justify-center shrink-0 mt-0.5">
                    <Check size={14} className="text-navy-dark" />
                  </div>
                  <span className="text-sm text-navy/80 leading-relaxed">{f}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="order-1 lg:order-2 img-zoom rounded-2xl shadow-xl">
            <img src={data.gallery[1]} alt={data.title} className="w-full h-[440px] object-cover rounded-2xl" />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-20">
        <div className="max-w-4xl mx-auto px-6">
          <SectionHeading center eyebrow="FAQ" title="Frequently asked questions" />
          <div className="space-y-4">
            {data.faq.map((item) => (
              <div key={item.q} className="rounded-xl border border-border p-6 hover:border-gold transition-colors">
                <h3 className="font-heading font-bold text-navy flex items-center gap-2 mb-2">
                  <HelpCircle size={18} className="text-gold-dark" /> {item.q}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed pl-7">{item.a}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <p className="text-muted-foreground mb-4">Have another question? Call us at</p>
            <a href={`tel:${company.phoneRaw}`} className="inline-flex items-center gap-2 font-heading font-bold text-navy text-2xl hover:text-navy-light transition-colors">
              <Phone size={22} className="text-gold" /> {company.phone}
            </a>
          </div>
        </div>
      </section>

      <CTABanner
        title={`Ready to get started with ${data.title}?`}
        subtitle="Fill in our inquiry form and our Client Engagement Team will contact you within 24 hours."
      />
    </div>
  );
};

export default ServicePage;
